export type ReportTemplate =
  | "pipeline-health"
  | "forecast-accuracy"
  | "rep-performance"
  | "lead-source-roi"
  | "churn-expansion"
  | "custom";

import type { TimeRange } from "@/components/shared/TimeRangeSelector";

export interface ReportFilters {
  /** Calendar / fiscal time range, same shape used by the Deals view. */
  timeRange: TimeRange;
  /** "all" or a rep display name. */
  owner: string;
}

export const DEFAULT_TIME_RANGE: TimeRange = {
  key: "this-quarter",
  label: "This Quarter",
  from: null,
  to: null,
};

export const DEFAULT_FILTERS: ReportFilters = {
  timeRange: DEFAULT_TIME_RANGE,
  owner: "all",
};

export interface SavedReport {
  /** Stable ID for favorites */
  id: string;
  template: ReportTemplate;
  title: string;
  description?: string;
  /** Original natural-language prompt that generated this report */
  prompt: string;
  filters: ReportFilters;
  /** ISO timestamp when last regenerated */
  generatedAt: string;
  /** For custom reports: ordered list of block ids from the block registry. */
  customBlocks?: string[];
}

export const REPORT_TEMPLATE_META: Record<Exclude<ReportTemplate, "custom">, { title: string; subtitle: string; samplePrompt: string }> = {
  "pipeline-health": {
    title: "Pipeline Health & Stage Conversion",
    subtitle: "Funnel, stage conversion, and at-risk deals for the selected period.",
    samplePrompt: "Show me pipeline health and stage conversion for this quarter",
  },
  "forecast-accuracy": {
    title: "Forecast vs Actual & Commit Accuracy",
    subtitle: "Compares forecast, commit, and actuals over time and by rep.",
    samplePrompt: "Show me forecast vs actual and commit accuracy this quarter",
  },
  "rep-performance": {
    title: "Rep Performance & Activity Impact",
    subtitle: "Connects activity levels to outcomes for each rep.",
    samplePrompt: "Show me rep performance and activity impact this quarter",
  },
  "lead-source-roi": {
    title: "Lead Source Performance & ROI",
    subtitle: "Pipeline and revenue by channel, with conversion and ROI.",
    samplePrompt: "Show me lead source performance and ROI this quarter",
  },
  "churn-expansion": {
    title: "Revenue Retention & Expansion Insights",
    subtitle: "Churn, contraction, renewals, and expansion by segment and owner.",
    samplePrompt: "Show me revenue retention, churn, and expansion this quarter",
  },
};